import React, { useEffect, useState, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootDispatch, RootState } from "../../store";
import { Loader2, X } from "lucide-react";
import {
  addRepayment,
  getAllRepayments,
} from "../../store/slices/commissionAndRepaymentSlice";
import toast from "react-hot-toast";
import Select from "react-select";
import { useInfiniteSalesReps } from "../../hooks/useInfiniteSalesReps";
import { useInfiniteCustomers } from "../../hooks/useInfiniteCustomers";
import Button, { ButtonType } from "../Assets/Button";

interface AddRepaymentModalProps {
  open: boolean;
  onClose: () => void;
  currentPage?: number;
  itemsPerPage?: number;
  search?: string;
  status?: string;
  from: string;
}

const AddRepaymentModal: React.FC<AddRepaymentModalProps> = ({
  open,
  onClose,
  currentPage = 1,
  itemsPerPage = 10,
  search = "",
  status,
  from,
}) => {
  const dispatch: RootDispatch = useDispatch();

  const addingRepayment = useSelector(
    (state: RootState) =>
      state.commissionAndRepayment.loadingState.addingRepaymentLoading,
  );

  const [selectedSalesRepId, setSelectedSalesRepId] = useState("");
  const [selectedCustomerId, setSelectedCustomerId] = useState("");
  const [isChargesApplied, setIsChargesApplied] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState<number>(0);

  const {
    salesReps: infiniteSalesReps,
    isLoadingMore: isLoadingMoreSalesReps,
    hasMore: hasMoreSalesReps,
    loadMore: loadMoreSalesReps,
    handleSearch: handleSalesRepSearch,
    reset: resetSalesReps,
  } = useInfiniteSalesReps(true);

  const {
    customers: infiniteCustomers,
    isLoadingMore: isLoadingMoreCustomers,
    hasMore: hasMoreCustomers,
    loadMore: loadMoreCustomers,
    handleSearch: handleCustomerSearch,
    reset: resetCustomers,
    handleSalesRepFilter,
  } = useInfiniteCustomers(20);

  useEffect(() => {
    setSelectedCustomerId("");

    if (selectedSalesRepId) {
      handleSalesRepFilter(selectedSalesRepId);
    } else {
      handleSalesRepFilter("");
    }
  }, [selectedSalesRepId, handleSalesRepFilter]);

  const salesRepOptions = infiniteSalesReps.map((rep: any) => ({
    value: rep._id,
    label: rep.name,
  }));

  const customerOptions = infiniteCustomers.map((customer: any) => ({
    value: customer._id,
    label: customer.customerName,
  }));

  const SalesRepMenuList = (props: any) => {
    const { children, ...rest } = props;
    const scrollRef = useRef<HTMLDivElement>(null);

    const handleScroll = useCallback(
      (event: React.UIEvent<HTMLDivElement>) => {
        const target = event.target as HTMLDivElement;
        const bottom =
          target.scrollHeight - target.scrollTop <= target.clientHeight + 5;

        if (bottom && hasMoreSalesReps && !isLoadingMoreSalesReps) {
          loadMoreSalesReps();
        }
      },
      [hasMoreSalesReps, isLoadingMoreSalesReps, loadMoreSalesReps],
    );

    return (
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        style={{ maxHeight: "300px", overflowY: "auto" }}
      >
        {children}
        {isLoadingMoreSalesReps && (
          <div className="flex justify-center items-center p-3">
            <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
            <span className="ml-2 text-xs text-gray-500">Loading more...</span>
          </div>
        )}
      </div>
    );
  };

  const CustomerMenuList = (props: any) => {
    const { children, ...rest } = props;
    const scrollRef = useRef<HTMLDivElement>(null);

    const handleScroll = useCallback(
      (event: React.UIEvent<HTMLDivElement>) => {
        const target = event.target as HTMLDivElement;
        const bottom =
          target.scrollHeight - target.scrollTop <= target.clientHeight + 5;

        if (bottom && hasMoreCustomers && !isLoadingMoreCustomers) {
          loadMoreCustomers();
        }
      },
      [hasMoreCustomers, isLoadingMoreCustomers, loadMoreCustomers],
    );

    return (
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        style={{ maxHeight: "300px", overflowY: "auto" }}
      >
        {children}
        {isLoadingMoreCustomers && (
          <div className="flex justify-center items-center p-3">
            <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
            <span className="ml-2 text-xs text-gray-500">Loading more...</span>
          </div>
        )}
      </div>
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedSalesRepId) {
      toast.error("Please select a sales representative");
      return;
    }
    if (!selectedCustomerId) {
      toast.error("Please select a customer");
      return;
    }
    if (!paymentAmount || paymentAmount <= 0) {
      toast.error("Please enter a valid payment amount");
      return;
    }

    try {
      await dispatch(
        addRepayment({
          salesRepId: selectedSalesRepId,
          customerId: selectedCustomerId,
          isChargesApplied,
          paymentAmount,
        }),
      ).unwrap();

      await dispatch(
        getAllRepayments({
          page: currentPage,
          limit: itemsPerPage,
          search,
          status,
          from,
        }),
      );

      handleClose();
    } catch (error: any) {
      toast.error(error || "Failed to add repayment");
    }
  };

  const handleClose = () => {
    setSelectedSalesRepId("");
    setSelectedCustomerId("");
    setIsChargesApplied(false);
    setPaymentAmount(0);
    resetSalesReps();
    resetCustomers();
    onClose();
  };

  const selectedSalesRepValue = salesRepOptions.find(
    (opt) => opt.value === selectedSalesRepId,
  );

  const selectedCustomerValue = customerOptions.find(
    (opt) => opt.value === selectedCustomerId,
  );

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-30 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md max-h-[60vh] overflow-auto flex flex-col">
        {/* Header */}
        <div className="bg-gray-100 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                Add New Repayment
              </h2>
              <p className="text-gray-500 text-sm">
                Create a new repayment entry
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            disabled={addingRepayment}
            className="text-gray-600 hover:text-gray-900 transition-colors p-1"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form
          onSubmit={handleSubmit}
          className="flex flex-col flex-1 mt-4 text-xs p-4"
        >
          <div className="space-y-4">
            <div>
              <label className="block font-medium text-gray-700 mb-1">
                Sales Representative *
              </label>
              <Select
                options={salesRepOptions}
                value={selectedSalesRepValue}
                onChange={(selectedOption: any) => {
                  setSelectedSalesRepId(selectedOption?.value || "");
                }}
                onInputChange={(inputValue, actionMeta) => {
                  if (actionMeta.action === "input-change") {
                    handleSalesRepSearch(inputValue);
                  }
                }}
                isClearable
                isDisabled={addingRepayment}
                placeholder="Search and select sales rep..."
                components={{ MenuList: SalesRepMenuList }}
                menuPortalTarget={document.body}
                menuPosition="fixed"
                styles={{
                  control: (base: any) => ({
                    ...base,
                    borderColor: "#e5e7eb",
                    boxShadow: "none",
                    "&:hover": {
                      borderColor: "#d1d5db",
                    },
                  }),
                  menu: (base: any) => ({
                    ...base,
                    zIndex: 9999,
                  }),
                  menuPortal: (base: any) => ({
                    ...base,
                    zIndex: 9999,
                  }),
                }}
              />
            </div>

            <div>
              <label className="block font-medium text-gray-700 mb-1">
                Customer *
              </label>
              <Select
                options={customerOptions}
                value={selectedCustomerValue}
                onChange={(selectedOption: any) => {
                  setSelectedCustomerId(selectedOption?.value || "");
                }}
                onInputChange={(inputValue, actionMeta) => {
                  if (actionMeta.action === "input-change") {
                    handleCustomerSearch(inputValue);
                  }
                }}
                isClearable
                isDisabled={addingRepayment || !selectedSalesRepId}
                placeholder="Search and select customer..."
                components={{ MenuList: CustomerMenuList }}
                menuPortalTarget={document.body}
                menuPosition="fixed"
                styles={{
                  control: (base: any) => ({
                    ...base,
                    minHeight: "40px",
                    borderColor: "#e5e7eb",
                    boxShadow: "none",
                    "&:hover": {
                      borderColor: "#d1d5db",
                    },
                  }),

                  menu: (base: any) => ({
                    ...base,
                    zIndex: 9999,
                  }),
                  menuPortal: (base: any) => ({
                    ...base,
                    zIndex: 9999,
                  }),
                }}
              />
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="isChargesApplied"
                checked={isChargesApplied}
                onChange={(e) => setIsChargesApplied(e.target.checked)}
                className="h-4 w-4 text-sky-600 focus:ring-sky-500 border-gray-300 rounded"
                disabled={addingRepayment}
              />
              <label
                htmlFor="isChargesApplied"
                className="ml-2 block text-xs text-gray-700"
              >
                Apply Charges (File & Portal Charges)
              </label>
            </div>

            <div>
              <label className="block font-medium text-gray-700 mb-1">
                Payment Amount (₹) *
              </label>
              <input
                type="number"
                value={paymentAmount}
                onChange={(e) => setPaymentAmount(Number(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500"
                placeholder="Enter payment amount"
                min="0"
                step="1"
                required
                disabled={addingRepayment}
              />
            </div>
          </div>

          <div className="flex items-end gap-3 mt-auto pt-4 border-t">
            <Button
              type={ButtonType.Cancel}
              onClick={handleClose}
              className="flex-1 text-xs"
              disabled={addingRepayment}
            >
              Cancel
            </Button>
            <Button
              type={ButtonType.Submit}
              disabled={addingRepayment}
              onClick={handleSubmit}
              className="flex-1 text-xs"
            >
              {addingRepayment ? (
                <Loader2 className="w-4 h-4 animate-spin inline mr-2" />
              ) : null}
              Add Repayment
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddRepaymentModal;
